import { action, computed, get } from "@ember/object"
import { next } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import * as Sentry from "@sentry/browser"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { User } from "@shared/models/user"
import ObjectId from "@shared/util/object-id-gen"
import HasManyAltogether from "cc-frontend/helpers/has-many-altogether"
import { Statechart } from "cc-frontend/helpers/statechart"
import COURSE_DATE_SAVE_AS_TEMPLATE, {
  insertNewTemplate,
  prepareSaveAsLessonTemplate,
} from "cc-frontend/lib/actions/course/calendar/COURSE_DATE_SAVE_AS_TEMPLATE"
import { prepareCourseAssignLessonTemplateToWeek } from "cc-frontend/lib/actions/course/COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK"
import { PLANBOOK_DELETE_TEMPLATE } from "cc-frontend/lib/actions/planbook/PLANBOOK_DELETE_TEMPLATE"
import type EmberStore from "cc-frontend/services/ember-store"
import type Session from "cc-frontend/services/session"
import { generateBlankTemplateAttributes } from "cc-frontend/utils/card-stack/blank"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { easeIn, easeOut } from "ember-animated/easings/cosine"
import move from "ember-animated/motions/move"
import { fadeIn, fadeOut } from "ember-animated/motions/opacity"
import { use } from "ember-could-get-used-to-this"
import { compact, filter, find, flatMap, forEach, includes, isFunction, isNil } from "lodash-es"
import type { MachineConfig } from "xstate"
import styles from "./main.module.css"

const forwardTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: -window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}
const backwardsTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: -window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}

interface Args {
  planbook: Planbook
  rotationCalendar: RotationCalendar
  close: Function
  setSmallWidth: Function
  setNormalWidth: Function
  isEditingFlow: boolean
  isCreatingFlow: boolean
  initialScreen: StateEvent
  stepName: string | null
  updateStepName?: Function
}

interface StateSchema {
  states: {
    STATE_ROOT: {}
    STATE_TITLE_BACKGROUND: {}
    STATE_COURSE_ONBOARDING: {}
    STATE_COURSES_EDITOR: {}
    STATE_SCHEDULE: {}
    STATE_TEMPLATES_ONBOARDING: {}
    STATE_SCHEDULE_ONBOARDING: {}
    STATE_TEMPLATES_BY_COURSE: {}
    STATE_TEMPLATES_BY_NAME: {}
    STATE_TEMPLATE_GALLERY: {}
    STATE_EDIT_TEMPLATE: {}
    STATE_FINAL_SCREEN: {}
    STATE_AFTER_APPLYING_FIRST_TEMPLATE: {}
  }
}

interface StateContext {
  queryParamCounter: string | number | undefined | null
  lastCount?: number
}

type StateEvent =
  | { type: "STATE_TITLE_BACKGROUND" }
  | { type: "STATE_COURSE_ONBOARDING" }
  | { type: "STATE_COURSES_EDITOR" }
  | { type: "STATE_TEMPLATES_ONBOARDING" }
  | { type: "STATE_SCHEDULE_ONBOARDING" }
  | { type: "STATE_SCHEDULE" }
  | { type: "STATE_TEMPLATES_BY_COURSE" }
  | { type: "STATE_TEMPLATES_BY_NAME" }
  | { type: "STATE_TEMPLATE_GALLERY" }
  | { type: "STATE_EDIT_TEMPLATE" }
  | { type: "STATE_AFTER_APPLYING_FIRST_TEMPLATE" }
  | { type: "next" }
  | { type: "prev" }
  | { type: "upToRoot" }
  | { type: "edit" }
  | { type: "cancel" }
  | { type: "done" }

const stateChartConfiguration: MachineConfig<StateContext, StateSchema, StateEvent> = {
  id: "planbook-create",
  initial: "STATE_ROOT",
  states: {
    STATE_ROOT: {
      on: {
        STATE_TITLE_BACKGROUND: "STATE_TITLE_BACKGROUND",
        STATE_COURSE_ONBOARDING: "STATE_COURSE_ONBOARDING",
        STATE_COURSES_EDITOR: "STATE_COURSES_EDITOR",
        STATE_SCHEDULE_ONBOARDING: "STATE_SCHEDULE_ONBOARDING",
        STATE_TEMPLATES_ONBOARDING: "STATE_TEMPLATES_ONBOARDING",
        STATE_AFTER_APPLYING_FIRST_TEMPLATE: "STATE_AFTER_APPLYING_FIRST_TEMPLATE",
        STATE_EDIT_TEMPLATE: "STATE_EDIT_TEMPLATE",
        STATE_TEMPLATE_GALLERY: "STATE_TEMPLATE_GALLERY",
        STATE_SCHEDULE: "STATE_SCHEDULE",
        STATE_TEMPLATES_BY_COURSE: "STATE_TEMPLATES_BY_COURSE",
        STATE_TEMPLATES_BY_NAME: "STATE_TEMPLATES_BY_NAME",
      },
    },
    STATE_TITLE_BACKGROUND: {
      onEntry: ["setNormalWidth"],
      on: {
        next: "STATE_COURSE_ONBOARDING",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_COURSE_ONBOARDING: {
      on: {
        next: "STATE_COURSES_EDITOR",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_COURSES_EDITOR: {
      on: {
        prev: "STATE_COURSE_ONBOARDING",
        next: "STATE_SCHEDULE_ONBOARDING",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_SCHEDULE_ONBOARDING: {
      on: {
        prev: "STATE_COURSES_EDITOR",
        next: "STATE_SCHEDULE",
        upToRoot: "STATE_ROOT",
      },
    },

    STATE_SCHEDULE: {
      onEntry: ["setNormalWidth"],
      on: {
        prev: "STATE_SCHEDULE_ONBOARDING",
        next: [
          // When unified lessons is enabled, skip the template screens entirely
          { target: "STATE_FINAL_SCREEN", cond: "shouldSkipTemplates" },
          { target: "STATE_TEMPLATES_ONBOARDING" },
        ],
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_TEMPLATES_ONBOARDING: {
      on: {
        prev: "STATE_SCHEDULE",
        next: "STATE_TEMPLATE_GALLERY",
        upToRoot: "STATE_ROOT",
      },
      onExit: ["notifyLeavingTemplateOnboarding"],
    },
    STATE_TEMPLATES_BY_COURSE: {
      onEntry: ["setNormalWidth"],
      on: {
        prev: {
          actions: ["goBackToPreviousState"],
        },
        next: "STATE_FINAL_SCREEN",
        STATE_TEMPLATES_ONBOARDING: "STATE_TEMPLATES_ONBOARDING",
        STATE_TEMPLATE_GALLERY: "STATE_TEMPLATE_GALLERY",
        STATE_EDIT_TEMPLATE: "STATE_EDIT_TEMPLATE",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_TEMPLATES_BY_NAME: {
      onEntry: ["setNormalWidth"],
      on: {
        STATE_EDIT_TEMPLATE: "STATE_EDIT_TEMPLATE",
        prev: "STATE_TEMPLATES_BY_COURSE",
        next: "STATE_FINAL_SCREEN",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_TEMPLATE_GALLERY: {
      onEntry: ["setNormalWidth", "saveSendingState"],
      on: {
        STATE_TEMPLATES_BY_COURSE: "STATE_TEMPLATES_BY_COURSE",
        STATE_TEMPLATES_ONBOARDING: "STATE_TEMPLATES_ONBOARDING",
        STATE_AFTER_APPLYING_FIRST_TEMPLATE: "STATE_AFTER_APPLYING_FIRST_TEMPLATE",
        upToRoot: "STATE_ROOT",
        prev: {
          actions: ["goBackToPreviousState"],
        },
      },
    },
    STATE_AFTER_APPLYING_FIRST_TEMPLATE: {
      on: {
        next: "STATE_TEMPLATES_BY_COURSE",
        prev: "STATE_TEMPLATE_GALLERY",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_EDIT_TEMPLATE: {
      onEntry: ["setSmallWidth", "saveSendingState"],
      on: {
        done: {
          actions: ["goBackToPreviousState"],
        },
        STATE_TEMPLATES_BY_COURSE: "STATE_TEMPLATES_BY_COURSE",
        STATE_TEMPLATES_BY_NAME: "STATE_TEMPLATES_BY_NAME",
        upToRoot: "STATE_ROOT",
      },
    },
    STATE_FINAL_SCREEN: {
      on: {
        prev: [
          // When unified lessons is enabled, the template screens were skipped
          { target: "STATE_SCHEDULE", cond: "shouldSkipTemplates" },
          { target: "STATE_TEMPLATES_BY_COURSE" },
        ],
        next: {
          actions: "close",
        },
        upToRoot: "STATE_ROOT",
      },
    },
  },
}

export default class PlanbookCreateEditMainComponent extends Component<Args> {
  readonly styles = styles

  @service("ember-store") declare store: EmberStore
  @service declare session: Session

  @service dispatcher

  @use courses = new HasManyAltogether<Course>(() => [this.args.planbook, "courses", "course"])
  @use cardStackTemplates = new HasManyAltogether<CardStack>(() => [
    this.args.planbook,
    "cardStackTemplates",
    "cardStack",
  ])

  @tracked templateIdToEdit: string | null = null
  @tracked courseIdToAssignTemplateTo: string | null = null
  @tracked previousState: string | null = null
  @tracked sendingStateFromTemplates: string | null = null
  @tracked lastCount: number = 0
  previousStates: Array<string> = []
  lastModalStepArg = null

  @use statechart = new Statechart<StateContext, StateSchema, { type: string }>(() => [
    {
      owner: this,
      chart: stateChartConfiguration,
      context: {
        queryParamCounter: this.args.stepName,
      },
      onTransition: (state) => {
        let currentValue = state.value
        if (
          state.changed === false ||
          currentValue === undefined ||
          currentValue === "STATE_ROOT" ||
          this.args.stepName === currentValue
        )
          return

        if (currentValue !== this.previousState) {
          // -2 is b/c we subtract 1 to get the lsat element and one more to get the elementn
          // before that one, which in this case, is the previous state before the current state
          if (this.previousStates[this.previousStates.length - 2] === currentValue) {
            // If we're going backwards, we remove the last state as we already have this state
            this.previousStates.pop()
          } else {
            this.previousStates.push(currentValue as string)
          }

          this.previousState = currentValue as string

          // Update the query param
          if (isFunction(this.args.updateStepName)) {
            this.args.updateStepName(currentValue)
          }
        }
      },
      update: (send, context, value) => {
        if (value === "STATE_ROOT") return
        // Update the state to the value in the URL
        if (value !== this.args.stepName) {
          if (this.lastModalStepArg === this.args.stepName) return
          next(this, () => {
            this.previousState = this.args.stepName
            send("upToRoot")
            send(this.args.stepName)
          })
        }
      },
    },
  ])

  get isTemplateGallery() {
    return this.statechart.state.matches("STATE_TEMPLATE_GALLERY")
  }

  get isEditingTemplate() {
    return this.statechart.state.matches("STATE_EDIT_TEMPLATE")
  }

  get isAfterApplyingFirstTemplate() {
    return this.statechart.state.matches("STATE_AFTER_APPLYING_FIRST_TEMPLATE")
  }

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal < newVal) {
      return forwardTransition
    } else {
      return backwardsTransition
    }
  }

  @computed("args.planbook.relationships.courses.data.length")
  get canAdvanceFromEditCourses() {
    return get(this.args.planbook.relationships.courses, "data").length > 0
  }

  get canAdvanceFromEditTemplates() {
    let content = this.courses.content
    if (isNil(content)) return false
    const instructionalCourses = filter(content, (course) => !course.attributes.isNonInstructional)
    let courseTemplateIds = compact(
      flatMap(instructionalCourses, (course) => extractCardStackTemplateIds(course)),
    )
    return courseTemplateIds.length >= instructionalCourses.length
  }

  @action shouldSkipTemplates() {
    // Skip the initial template picker screen if we want them to use the Add Lesson menu instead.
    // This requires unified lessons, since they are what show the menu.
    const hasUnifiedLessons = !!this.args.planbook.attributes.flags.unifiedLessons
    const hasFeature = !!(this.session.user?.content as User | undefined)?.attributes?.featureFlags[
      "ADD_LESSON_MENU"
    ]
    return hasUnifiedLessons && hasFeature
  }

  @action prev() {
    this.statechart.send("prev")
  }
  @action next() {
    this.statechart.send("next")
  }
  @action done() {
    this.statechart.send("done")
  }

  @action goToScreen(screen: StateEvent) {
    this.statechart.send("upToRoot")
    this.statechart.send(screen)
  }

  @action close() {}

  /**
   *  For templates in a course where you might or might not own it
   */
  @action editCourseTemplate(courseId: string, templateId: string) {
    // find out about the template
    this.store.find("card-stack", templateId).then((cardStack) => {
      if (cardStack.relationships.planbook.data.id === this.args.planbook.id) {
        // we own the planbook
        this.templateIdToEdit = templateId
        this.statechart.send("STATE_EDIT_TEMPLATE")
      } else {
        // copy the template
        let newTemplateId = ObjectId.create()
        // First set the new template for all days that have the existing card stack id
        let course = find(this.courses.content, (course) => course.id === courseId)
        if (isNil(course)) return
        let newCardStackAttributes = cloneCardStackAttributesWithNewIds(
          cardStack.attributes,
          cardStack.attributes.parentCardStack,
          !!this.args.planbook.attributes.flags.unifiedLessons,
          {
            overrideTitle: `My ${course.attributes.title} Template`,
            onMigrationError: (error) => Sentry.captureException(error),
          },
        )
        insertNewTemplate(this.store, newTemplateId, this.args.planbook.id, newCardStackAttributes)
        this.dispatcher.dispatchPreparedAction(
          prepareCourseAssignLessonTemplateToWeek({
            course: course,
            cardStackId: newTemplateId,
            planbookId: this.args.planbook.id,
            shouldCreateNewTemplate: true,
            newTemplateAttrs: newCardStackAttributes,
            rotationCalendar: this.args.rotationCalendar,
          }),
        )
        this.templateIdToEdit = newTemplateId
        this.statechart.send("STATE_EDIT_TEMPLATE")
      }
    })
  }

  /**
   * Only for templates you own
   */
  @action editPlanbookTemplate(templateId: string) {
    // find out about the template
    this.store.find("card-stack", templateId).then((cardStack) => {
      if (cardStack.relationships.planbook.data.id === this.args.planbook.id) {
        // we own the planbook
        this.templateIdToEdit = templateId
        this.statechart.send("STATE_EDIT_TEMPLATE")
      } else {
        // copy the template
        let newCardStackAttributes = cloneCardStackAttributesWithNewIds(
          cardStack.attributes,
          cardStack.attributes.parentCardStack,
          !!this.args.planbook.attributes.flags.unifiedLessons,
          {
            overrideTitle: "My " + cardStack.attributes.title,
            onMigrationError: (error) => Sentry.captureException(error),
          },
        )
        let newTemplateId = ObjectId.create()
        // First set the new template for all days that have the existing card stack id
        insertNewTemplate(this.store, newTemplateId, this.args.planbook.id, newCardStackAttributes)
        this.dispatcher.dispatchAction(COURSE_DATE_SAVE_AS_TEMPLATE, {
          newId: newTemplateId,
          planbookId: this.args.planbook.id,
          cardStackAttributes: newCardStackAttributes,
        })
        forEach(
          filter(this.courses.content, (course) => {
            let ids = extractCardStackTemplateIds(course)
            return includes(ids, templateId)
          }),
          (course) => {
            if (isNil(course)) return
            this.dispatcher.dispatchPreparedAction(
              prepareCourseAssignLessonTemplateToWeek({
                course: course,
                cardStackId: newTemplateId,
                planbookId: this.args.planbook.id,
                shouldCreateNewTemplate: false,
                newTemplateAttrs: newCardStackAttributes,
                rotationCalendar: this.args.rotationCalendar,
              }),
            )
          },
        )

        this.templateIdToEdit = newTemplateId
        this.statechart.send("STATE_EDIT_TEMPLATE")
      }
    })
  }

  @action createTemplate() {
    let templateAttrs = generateBlankTemplateAttributes(this.args.planbook)
    let newTemplateId = ObjectId.create()

    insertNewTemplate(this.store, newTemplateId, this.args.planbook.id, templateAttrs)
    this.dispatcher.dispatchPreparedAction(
      prepareSaveAsLessonTemplate({
        newId: newTemplateId,
        planbookId: this.args.planbook.id,
        cardStackAttributes: templateAttrs,
      }),
    )

    this.templateIdToEdit = newTemplateId
    this.statechart.send("STATE_EDIT_TEMPLATE")
  }

  @action createAndAssignTemplate(courseId) {
    let templateAttrs = generateBlankTemplateAttributes(this.args.planbook)
    let newTemplateId = ObjectId.create()

    let course = find(this.courses.content, (course) => course.id === courseId)
    if (isNil(course)) return
    insertNewTemplate(this.store, newTemplateId, this.args.planbook.id, templateAttrs)
    this.dispatcher.dispatchPreparedAction(
      prepareCourseAssignLessonTemplateToWeek({
        course: course,
        cardStackId: newTemplateId,
        planbookId: this.args.planbook.id,
        shouldCreateNewTemplate: true,
        newTemplateAttrs: templateAttrs,
        rotationCalendar: this.args.rotationCalendar,
      }),
    )
    this.templateIdToEdit = newTemplateId
    this.statechart.send("STATE_EDIT_TEMPLATE")
  }

  @action assignTemplateToCourse(courseId: string, templateId: string) {
    let course = find(this.courses.content, (course) => course.id === courseId)
    if (isNil(course)) return
    this.dispatcher.dispatchPreparedAction(
      prepareCourseAssignLessonTemplateToWeek({
        course: course,
        cardStackId: templateId,
        planbookId: this.args.planbook.id,
        rotationCalendar: this.args.rotationCalendar,
      }),
    )
  }

  @action toggleTemplateForCourse(courseId: string, templateId: string) {
    let course = find(this.courses.content, (course) => course.id === courseId)
    if (isNil(course)) return
    let existingTemplateIds = extractCardStackTemplateIds(course)
    let alreadyHasThisTemplate = includes(existingTemplateIds, templateId)
    this.dispatcher.dispatchPreparedAction(
      prepareCourseAssignLessonTemplateToWeek({
        course: course,
        cardStackId: alreadyHasThisTemplate ? null : templateId,
        planbookId: this.args.planbook.id,
        rotationCalendar: this.args.rotationCalendar,
      }),
    )
  }

  @action deleteTemplate(templateId) {
    this.dispatcher.dispatchAction(PLANBOOK_DELETE_TEMPLATE, {
      cardStackId: templateId,
      planbookId: this.args.planbook.id,
    })
  }
  @action goToTemplateGallery(courseId) {
    this.courseIdToAssignTemplateTo = courseId
    this.statechart.send("STATE_TEMPLATE_GALLERY")
  }

  selectedFromGalleryCount = 0
  @action selectFromGallery(cardStack: CardStack) {
    this.selectedFromGalleryCount++
    let courses =
      this.courseIdToAssignTemplateTo === "ALL"
        ? filter(this.courses.content, (course) => !course.attributes.isNonInstructional)
        : filter(this.courses.content, (course) => course.id === this.courseIdToAssignTemplateTo)

    forEach(courses, (course) => {
      this.dispatcher.dispatchPreparedAction(
        prepareCourseAssignLessonTemplateToWeek({
          course: course,
          cardStackId: cardStack.id,
          planbookId: this.args.planbook.id,
          rotationCalendar: this.args.rotationCalendar,
        }),
      )
    })
    if (this.args.isCreatingFlow && this.selectedFromGalleryCount === 1) {
      this.statechart.send("STATE_AFTER_APPLYING_FIRST_TEMPLATE")
    } else {
      // that should change if you're coming from templats by name
      this.statechart.send("STATE_TEMPLATES_BY_COURSE")
    }
  }

  @action setInitialState() {
    console.log(
      "setinitial state",
      this.args.initialScreen,
      this.statechart.state?.value,
      this.args.isCreatingFlow,
    )
    if (this.args.isCreatingFlow) {
      this.statechart.send("STATE_COURSE_ONBOARDING")
    } else {
      this.statechart.send(this.args.initialScreen)
    }
  }

  @action notifyLeavingTemplateOnboarding(conntext) {
    // prefetch the courses
    this.courses
    this.courseIdToAssignTemplateTo = "ALL"
  }

  @action saveSendingState(context, eventObject) {
    let lastState = this.statechart.state?.value
    if (lastState === undefined) return
    this.sendingStateFromTemplates = lastState as string
  }

  @action goBackToPreviousState(context) {
    if (isNil(this.sendingStateFromTemplates)) return
    this.statechart.send(this.sendingStateFromTemplates)
  }

  @action setSmallWidth(context, eventObject) {
    this.args.setSmallWidth()
  }

  @action setNormalWidth(context) {
    this.args.setNormalWidth()
  }
}
